import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SiteTag } from "@/components/Logo";
import { useSiteInfo } from "@/hooks/useSiteInfo";

export default function BookSuccess() {
    const [params] = useSearchParams();
    const groupId = params.get("group_id");
    const site = useSiteInfo();
    const [group, setGroup] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!groupId) {
            setError("Geen boeking-ID meegegeven.");
            return;
        }
        api.get(`/bookings/group/${groupId}`)
            .then((r) => setGroup(r.data))
            .catch((e) =>
                setError(
                    e?.response?.data?.detail ||
                        "Kon de boeking niet ophalen.",
                ),
            );
    }, [groupId]);

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-16">
            <div
                className="w-full max-w-3xl border border-[#e5e5e5] p-10 md:p-14"
                data-testid="success-card"
            >
                {!group && !error && (
                    <div className="flex flex-col items-start gap-4">
                        <Loader2
                            className="animate-spin text-[#ED6A14]"
                            size={32}
                        />
                        <h1 className="font-display font-bold text-3xl tracking-tight text-[#0A1F44]">
                            Je boeking opvragen…
                        </h1>
                    </div>
                )}
                {group && (
                    <div className="flex flex-col items-start gap-4">
                        <div className="w-12 h-12 bg-[#ED6A14] flex items-center justify-center">
                            <Check className="text-white" size={24} />
                        </div>
                        <SiteTag name={site.name} city={site.city} />
                        <p className="text-xs uppercase tracking-widest text-[#ED6A14]">
                            Boeking bevestigd
                        </p>
                        <h1 className="font-display font-bold text-4xl tracking-tighter text-[#0A1F44]">
                            {group.bookings.length === 1
                                ? "Tot binnenkort op " + site.name + "."
                                : `${group.bookings.length} wagens ingepland op ${site.name}.`}
                        </h1>
                        <p className="text-[#555] mt-2 text-sm">
                            We hebben je bevestiging per e-mail verstuurd
                            naar{" "}
                            <span className="font-medium text-[#0A1F44]">
                                {group.bookings[0].customer_email}
                            </span>
                            . De factuur volgt na de wasbeurt(en).
                        </p>

                        <div className="w-full mt-6 border-t border-[#e5e5e5] pt-6">
                            <table
                                className="w-full text-sm"
                                data-testid="success-table"
                            >
                                <thead>
                                    <tr className="text-left text-[#555] text-xs uppercase tracking-widest border-b border-[#e5e5e5]">
                                        <th className="py-2 pr-3">Plaat</th>
                                        <th className="py-2 pr-3">Dienst</th>
                                        <th className="py-2 pr-3">Datum</th>
                                        <th className="py-2 pr-3">Slot</th>
                                        <th className="py-2 pr-3 text-right">
                                            Bedrag
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.bookings.map((b) => (
                                        <tr
                                            key={b.id}
                                            className="border-b border-[#f0f0f0]"
                                        >
                                            <td className="py-3 pr-3 font-mono-ui text-[#0A1F44]">
                                                {b.license_plate}
                                            </td>
                                            <td className="py-3 pr-3 text-[#555]">
                                                {b.service_name}
                                            </td>
                                            <td className="py-3 pr-3 text-[#555]">
                                                {new Date(b.date).toLocaleDateString(
                                                    "nl-BE",
                                                    {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    },
                                                )}
                                            </td>
                                            <td className="py-3 pr-3 text-[#555]">
                                                {b.slot === "morning"
                                                    ? "Voormiddag"
                                                    : "Namiddag"}
                                            </td>
                                            <td className="py-3 pr-3 text-right text-[#0A1F44] font-medium">
                                                €
                                                {b.amount_eur
                                                    .toFixed(2)
                                                    .replace(".", ",")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="pt-4 text-right text-xs uppercase tracking-widest text-[#555]"
                                        >
                                            Totaal (incl. 21% btw)
                                        </td>
                                        <td className="pt-4 text-right">
                                            <span className="font-display font-bold text-2xl text-[#0A1F44]">
                                                €
                                                {group.total_eur
                                                    .toFixed(2)
                                                    .replace(".", ",")}
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <Link
                            to="/"
                            className="mt-8 inline-flex items-center gap-2 bg-[#0A1F44] text-white px-6 py-3 font-semibold hover:bg-[#061535] transition-colors"
                            data-testid="back-home-btn"
                        >
                            Terug naar home <ArrowRight size={16} />
                        </Link>
                    </div>
                )}
                {error && (
                    <div className="flex flex-col items-start gap-4">
                        <div className="w-12 h-12 bg-[#D92D20] flex items-center justify-center">
                            <AlertCircle className="text-white" size={24} />
                        </div>
                        <h1 className="font-display font-bold text-3xl tracking-tight text-[#0A1F44]">
                            Er ging iets mis
                        </h1>
                        <p className="text-[#555]">{error}</p>
                        <Link
                            to="/book"
                            className="mt-4 inline-flex items-center gap-2 bg-[#ED6A14] text-white px-6 py-3 font-semibold hover:bg-[#C9550C] transition-colors"
                        >
                            Opnieuw proberen <ArrowRight size={16} />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
